import { useEffect, useState } from 'react';
import type { CommandHandoverInboxItem } from '@tsr/shared-types';
import { fetchCommandHandoverInbox } from './api';
import {
  COMMAND_INBOX_HEADING,
  COMMAND_INBOX_SUBTEXT,
  commandHandoverInboxLabel,
} from './incidentVocabulary';

/**
 * INCOMING COMMAND CHANGES worklist (#481 MUST-1) — the RECEIVER's inbound queue of pending
 * command hand-overs addressed to their organization: §4.8 Transfers of Command + §4.7 Unified
 * Command invites (server-scoped to to_agency_id). Closes a ☎-escape: a pending hand-over used to
 * render ONLY on that incident's own panel, so the receiving jurisdiction could not FIND an
 * incoming transfer without a phone call. Mirrors InboxTasksPanel (a passive 30 s re-poll + an
 * open-count badge that stays live while another tab is active). A row opens the incident record
 * via the shipped `#incident=<id>` deep-link, where the receiving IC ACCEPTS on the existing
 * command-controls — NO new command path.
 *
 * DEMO NOTE: like the agency-tasks inbox, this filters by the ACTING agency. The single-operator
 * demo seat is the "Provincial Emergency Operations Centre (PEOC)" — a §4.2 COORDINATION seat that
 * by doctrine can NEVER hold or receive command — so no valid hand-over targets it and this list is
 * empty in the demo. The RECEIVER of a hand-over is a commanding Lead Organization (municipal /
 * provincial / First Nation), a DIFFERENT seat; the inbox READ + scoping is proven at the route
 * level acting as that target agency. Surfacing it live to that seat needs the enforced multi-seat
 * identity (the named #190 follow-on).
 */

export const COMMAND_HANDOVER_INBOX_POLL_MS = 30_000;

export function CommandHandoverInboxPanel({
  onActiveCountChange,
}: {
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [items, setItems] = useState<CommandHandoverInboxItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function reload(): Promise<void> {
      try {
        const list = await fetchCommandHandoverInbox();
        if (!cancelled) setItems(list);
      } catch {
        /* keep current */
      }
    }
    void reload();
    const id = setInterval(() => void reload(), COMMAND_HANDOVER_INBOX_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Every pending hand-over is actionable (awaiting this org's acceptance) → the whole list is open.
  useEffect(() => {
    onActiveCountChange?.(items.length);
  }, [items, onActiveCountChange]);

  if (items.length === 0) {
    // Render nothing when empty — this section is additive to the agency-task inbox, so an empty
    // command-hand-over queue leaves the Inbox tab reading exactly as it did before.
    return <></>;
  }

  return (
    <section
      data-testid="command-handover-inbox-panel"
      className="border-b border-slate-800 px-3 py-3"
    >
      <div className="mb-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-200">
          {COMMAND_INBOX_HEADING}
        </h3>
        <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{COMMAND_INBOX_SUBTEXT}</p>
      </div>
      <ul className="space-y-2">
        {items.map((h) => (
          <li
            key={h.id}
            data-testid="command-handover-row"
            data-kind={h.kind}
            className="rounded border border-amber-600/40 bg-amber-950/20"
          >
            {/* Open the incident record (the shipped `#incident=<id>` deep-link) where the receiving
                IC accepts on the existing command-controls. NOT a new command path. */}
            <a
              href={`#incident=${h.incident_id}`}
              data-testid="command-handover-open"
              title={`Open ${h.incident_number} to accept`}
              className="block px-3 py-2 hover:bg-amber-900/20"
            >
              <p className="text-sm font-medium text-slate-100">{commandHandoverInboxLabel(h.kind)}</p>
              <p className="text-[11px] text-slate-400">
                from <span className="text-slate-300">{h.from_agency_name}</span> · {h.incident_number}
              </p>
              {h.to_officer_name && (
                <p className="text-[10px] text-slate-500">Incident Commander: {h.to_officer_name}</p>
              )}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
